/**
 * Course detail configuration for individual course landing pages at /courses/[slug]
 * Each object contains all SEO + copy content needed to render a dedicated page.
 */

export const COURSE_DETAILS = {
  'tally-gst': {
    slug: 'tally-gst',
    name: 'Tally with GST',
    h1: 'Tally with GST Course in Kalaburagi (Gulbarga)',
    metaTitle: 'Tally with GST Course in Kalaburagi (Gulbarga) — Genius Academy',
    metaDescription:
      'Learn Tally Prime with GST at Genius Academy, Kalaburagi (Gulbarga). Practical accounting training, real invoicing, GST filing basics — 1:1 accountability, small batches. Book a free demo.',
    duration: '3 months',
    level: 'Beginner to Advanced',
    intro:
      'Tally is the most widely used accounting software across Indian businesses — from local retailers to established firms. Our Tally with GST course in Kalaburagi (Gulbarga) is designed to make you office-ready by combining core Tally operations with real-world GST practice.',
    covers: [
      'Company creation, ledgers, groups and voucher entry',
      'Purchase, sales, receipt, payment and journal vouchers',
      'Inventory management: stock groups, items and units',
      'GST configuration: CGST, SGST, IGST — practical setup',
      'GST invoicing, tax reports, GSTR-1 and GSTR-3B basics',
      'Bank reconciliation, TDS entries and payroll basics',
      'Balance Sheet, P&L, trial balance and financial reports',
      'Real-life scenarios: retail shop, service business, wholesale',
    ],
    who: [
      'PUC, degree students looking for a job-ready accounting skill',
      'Working professionals switching to accounts / finance roles',
      'Shop owners, entrepreneurs managing their own books',
      'Aspiring accountants preparing for interviews',
    ],
    outcomes: [
      'Handle day-to-day accounting for any small or medium business',
      'Generate GST-compliant invoices and tax reports',
      'Apply confidently for Accounts Executive / Junior Accountant roles',
      'Manage your own business finances without depending on others',
    ],
    faqs: [
      {
        q: 'Do I need an accounting background to join the Tally with GST course?',
        a: 'No. We start from the basics of accounting and gradually move into Tally Prime and GST. Even complete beginners have completed this course successfully at Genius Academy.',
      },
      {
        q: 'How long is the Tally with GST course?',
        a: 'The full course runs for approximately 3 months, with a mix of concept sessions and hands-on practice in our computer lab. Batch timings are flexible.',
      },
      {
        q: 'Will I get a certificate after completing the Tally course?',
        a: 'Yes. Every student who completes the syllabus receives a Genius Academy certificate that is verifiable online through our Certificate Verifier.',
      },
      {
        q: 'Do you cover the latest version of Tally?',
        a: 'Yes. We train on Tally Prime, which is the current version widely used by businesses across Kalaburagi (Gulbarga) and India.',
      },
    ],
  },

  'ms-office': {
    slug: 'ms-office',
    name: 'MS Office',
    h1: 'MS Office Course in Kalaburagi (Gulbarga)',
    metaTitle: 'MS Office Course in Kalaburagi (Gulbarga) — Word, Excel, PowerPoint',
    metaDescription:
      'Master MS Word, Excel, PowerPoint and Outlook with Genius Academy — the trusted computer institute in Kalaburagi (Gulbarga). Hands-on assignments, 1:1 attention. Book a free demo.',
    duration: '2 months',
    level: 'Beginner to Intermediate',
    intro:
      'MS Office is a non-negotiable skill in every modern workplace — banks, offices, schools, small businesses. Our MS Office course in Kalaburagi (Gulbarga) covers Word, Excel, PowerPoint and Outlook with practical assignments so you build real confidence, not just theory.',
    covers: [
      'MS Word: professional documents, formatting, tables, mail merge',
      'MS Excel: formulas, functions, VLOOKUP, pivot tables, charts',
      'MS Excel: data validation, conditional formatting, dashboards',
      'MS PowerPoint: presentations, transitions, master slides',
      'MS Outlook: email setup, calendar, meetings and contacts',
      'File management, printing, keyboard shortcuts, productivity',
      'Real-world templates: resumes, reports, invoices, budgets',
    ],
    who: [
      'Students preparing for job interviews and internships',
      'Office assistants, data-entry operators, receptionists',
      'Small business owners handling their own paperwork',
      'Anyone building foundational digital skills',
    ],
    outcomes: [
      'Create polished documents, spreadsheets and presentations',
      'Confidently handle office paperwork and reporting',
      'Qualify for entry-level office and data-entry roles',
      'Build a strong foundation for advanced Excel, Tally or programming courses',
    ],
    faqs: [
      {
        q: 'Do you cover Excel formulas like VLOOKUP and pivot tables?',
        a: 'Yes. Formulas, VLOOKUP / HLOOKUP, IF conditions, pivot tables and charts are a core part of the MS Excel module in our course.',
      },
      {
        q: 'How long does the MS Office course take?',
        a: 'About 2 months of regular classes covering Word, Excel, PowerPoint and Outlook, with hands-on practice in our computer lab.',
      },
      {
        q: 'Which version of MS Office do you teach?',
        a: 'We train on the current MS Office (365 / 2019+) with concepts that transfer to any version you may encounter at work.',
      },
      {
        q: 'Will I get a certificate after MS Office training?',
        a: 'Yes. Every student who finishes the course receives a Genius Academy certificate, verifiable online through our Certificate Verifier.',
      },
    ],
  },

  'python': {
    slug: 'python',
    name: 'Python',
    h1: 'Python Course in Kalaburagi (Gulbarga)',
    metaTitle: 'Python Course in Kalaburagi (Gulbarga) — Learn Python Programming',
    metaDescription:
      'Learn Python programming from scratch at Genius Academy, Kalaburagi (Gulbarga). Hands-on projects, personal mentoring and 1:1 accountability. Ideal for students and career switchers.',
    duration: '3-4 months',
    level: 'Beginner to Intermediate',
    intro:
      'Python is one of the most in-demand programming languages in the world — used in web development, data science, automation and AI. Our Python course in Kalaburagi (Gulbarga) builds strong fundamentals with hands-on projects, so you don\'t just learn syntax — you learn how to think like a programmer.',
    covers: [
      'Python setup, variables, data types and operators',
      'Control flow: if / else, loops, break and continue',
      'Functions, arguments, recursion and modules',
      'Lists, tuples, sets, dictionaries and comprehensions',
      'String handling, file input/output and error handling',
      'Object-oriented programming: classes, inheritance, polymorphism',
      'Popular libraries: NumPy, Pandas basics for data handling',
      'Mini projects: calculator, task manager, data-cleaning script',
    ],
    who: [
      'Students exploring programming as a career',
      'BCA, BE, BSc students strengthening their coding fundamentals',
      'Career switchers moving into tech / data roles',
      'Anyone curious about automation and problem-solving with code',
    ],
    outcomes: [
      'Write clean Python programs from scratch',
      'Solve real problems with logic, loops and functions',
      'Get a strong foundation for data science, web dev or AI',
      'Confidently attempt Python-based interviews and assessments',
    ],
    faqs: [
      {
        q: 'Do I need prior programming experience for the Python course?',
        a: 'No. Our Python course starts from the absolute basics and gradually moves to object-oriented programming and mini projects.',
      },
      {
        q: 'How long is the Python course at Genius Academy?',
        a: 'The course runs for about 3 to 4 months depending on the pace of the batch, covering core Python and introductory data-handling libraries.',
      },
      {
        q: 'Will I build real projects during the Python course?',
        a: 'Yes. You will work on hands-on mini projects like a calculator, a task manager and a simple data-cleaning script to solidify your learning.',
      },
      {
        q: 'Is this Python course suitable for data science later?',
        a: 'Yes. We build a strong foundation and introduce NumPy and Pandas basics, so you are well prepared for data science or web-dev tracks.',
      },
    ],
  },

  'typing': {
    slug: 'typing',
    name: 'Typing',
    h1: 'Typing Classes in Kalaburagi (Gulbarga)',
    metaTitle: 'Typing Classes in Kalaburagi (Gulbarga) — English Typing Speed & Accuracy',
    metaDescription:
      'Build fast, accurate English typing skills at Genius Academy Kalaburagi (Gulbarga). Structured daily practice, speed tests and personal guidance. Ideal for government exams and office jobs.',
    duration: '1-2 months',
    level: 'Beginner to Advanced',
    intro:
      'Typing is a critical skill for government exams, office jobs and everyday computer use. Our typing classes in Kalaburagi (Gulbarga) use structured daily practice sessions to build muscle memory, speed and accuracy — with clear speed targets and personal guidance.',
    covers: [
      'Correct posture, hand position and touch-typing technique',
      'Home row, top row and bottom row drills',
      'Capital letters, punctuation, numbers and symbols',
      'Speed building: WPM tracking, daily benchmarks',
      'Accuracy drills: error correction, rhythm and consistency',
      'Practice sets for exam patterns and office paragraphs',
      'Timed tests: 15, 30 and 60-minute mock sessions',
    ],
    who: [
      'Government exam aspirants (SSC, banking, state exams)',
      'Students preparing for typing tests as part of recruitment',
      'Office staff, data-entry operators, receptionists',
      'Anyone who wants to use a computer faster and more confidently',
    ],
    outcomes: [
      'Reach comfortable typing speeds of 25–40+ WPM (with continued practice)',
      'Improve accuracy through structured, repeatable drills',
      'Prepare for typing-based government and office recruitment tests',
      'Save hours every week by typing without looking at the keyboard',
    ],
    faqs: [
      {
        q: 'Do you provide typing practice for government exams?',
        a: 'Yes. Our typing classes include exam-style paragraphs and timed practice sets that mirror the kind of tests used in government and office recruitment.',
      },
      {
        q: 'How long does it take to reach a good typing speed?',
        a: 'Most students reach a comfortable everyday speed within 4 to 8 weeks of consistent daily practice at our institute.',
      },
      {
        q: 'What typing speed can I realistically achieve?',
        a: 'With regular practice, students typically move from 10–15 WPM to 25–40+ WPM. Exact speed depends on daily practice and consistency.',
      },
      {
        q: 'Do you provide a typing certificate?',
        a: 'Yes. Every student who completes the course receives a Genius Academy certificate that can be verified online through our Certificate Verifier.',
      },
    ],
  },

  'photoshop': {
    slug: 'photoshop',
    name: 'Photoshop',
    h1: 'Photoshop Course in Kalaburagi (Gulbarga)',
    metaTitle: 'Photoshop Course in Kalaburagi (Gulbarga) — Photo Editing & Graphic Design',
    metaDescription:
      'Learn Adobe Photoshop at Genius Academy Kalaburagi (Gulbarga). Practical photo editing, retouching, poster design and social media creatives. 1:1 attention, small batches.',
    duration: '2 months',
    level: 'Beginner to Intermediate',
    intro:
      'Adobe Photoshop is the industry standard for photo editing, poster design and social media creatives. Our Photoshop course in Kalaburagi (Gulbarga) is highly practical — you learn by editing real photos and designing real posters, not by watching endless theory.',
    covers: [
      'Photoshop interface, tools panel and workspace setup',
      'Layers, masks, selections and non-destructive editing',
      'Photo retouching: skin, background removal, colour correction',
      'Text, typography, effects and layer styles',
      'Poster design, social media creatives, banners, flyers',
      'Basic photo manipulation and creative compositions',
      'Exporting for print and web (JPG, PNG, PDF)',
    ],
    who: [
      'Aspiring graphic designers and social media creators',
      'Photographers looking to add editing to their skill set',
      'Small business owners designing their own marketing material',
      'Students exploring visual design as a career',
    ],
    outcomes: [
      'Edit and retouch photos to a professional standard',
      'Design posters, banners and social media creatives',
      'Take on freelance graphic-design projects',
      'Move confidently into CorelDRAW / advanced design courses',
    ],
    faqs: [
      {
        q: 'Do I need a design background to learn Photoshop?',
        a: 'Not at all. Our Photoshop course starts from the interface and basic tools, and gradually moves into advanced editing and design work.',
      },
      {
        q: 'How long is the Photoshop course?',
        a: 'The full course takes approximately 2 months with a strong emphasis on hands-on practice and real design assignments.',
      },
      {
        q: 'Do you teach Photoshop CS or Photoshop CC?',
        a: 'We train on a current version of Adobe Photoshop CC, with concepts that transfer to any recent version of the software.',
      },
      {
        q: 'Will I be able to take on freelance work after this course?',
        a: 'Many of our students confidently take up small freelance and local design projects after building their skills through practical assignments.',
      },
    ],
  },

  'computer-hardware': {
    slug: 'computer-hardware',
    name: 'Computer Hardware',
    h1: 'Computer Hardware Course in Kalaburagi (Gulbarga)',
    metaTitle: 'Computer Hardware Course in Kalaburagi (Gulbarga) — Assembly & Repair',
    metaDescription:
      'Learn computer hardware, assembly and repair at Genius Academy Kalaburagi (Gulbarga). Hands-on troubleshooting, real components, small batches. Perfect for freshers and technicians.',
    duration: '3 months',
    level: 'Beginner to Advanced',
    intro:
      'Every home, shop and office runs on computers — and someone needs to keep them running. Our computer hardware course in Kalaburagi (Gulbarga) is deeply practical: you handle real components, assemble machines and troubleshoot common faults with expert guidance.',
    covers: [
      'Computer components: CPU, motherboard, RAM, storage, PSU',
      'Assembly and disassembly of desktop computers',
      'BIOS configuration and boot sequence',
      'Operating system installation: Windows and drivers',
      'Peripheral setup: printer, scanner, network devices',
      'Troubleshooting: no-boot, no-display, slow-PC issues',
      'Basic networking: LAN, IP configuration, sharing',
      'Preventive maintenance and safe handling practices',
    ],
    who: [
      'Students planning a career in IT support or repair',
      'Freshers looking for jobs at computer shops and service centres',
      'Small business owners maintaining their own systems',
      'Hobbyists who want to build or upgrade their own PC',
    ],
    outcomes: [
      'Assemble and configure a desktop computer end-to-end',
      'Diagnose and fix common hardware and software issues',
      'Set up peripherals, networks and shared resources',
      'Start a career or a small hardware / service business',
    ],
    faqs: [
      {
        q: 'Do I need any technical background for the computer hardware course?',
        a: 'No prior background is required. The course is designed for absolute beginners and gradually moves into advanced troubleshooting.',
      },
      {
        q: 'Will I get hands-on practice with real hardware?',
        a: 'Yes. Assembly, disassembly and troubleshooting are done on real components in our lab — not just theory.',
      },
      {
        q: 'How long is the computer hardware course?',
        a: 'The full course runs for approximately 3 months, including both hardware and basic networking modules.',
      },
      {
        q: 'Is this course useful for opening my own computer repair shop?',
        a: 'Yes. Many of our students use this course as the foundation for a small hardware, repair or IT-service business in and around Kalaburagi.',
      },
    ],
  },
};

export const COURSE_SLUGS = Object.keys(COURSE_DETAILS);
